import Offer, { OrderDiscountOffer, NewDiscountType } from '@baemingo/offer';
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { v4 as uuid } from 'uuid';
import { DISCOUNT_TYPE, SERVICE_TYPE } from '~../__queries__';
import { useI18n, useSelector } from '~Hooks';
import { CartItem, LocalItem, TicketMessage } from '~Types';
import { CombinedItem } from '~Utils/combineArticles';
import { selectItem } from '~Utils/itemSelection';
import { useStorage } from '~Components/Storage';
import { isServiceChargeItem } from '~Utils/serviceCharge';
import { MEMBERSHIP_DISCOUNT_ID } from '~Components/LinkUser/consts';
import { useItemsPackager, getDiscountExcludedCategoryIds } from '~Utils/packagedItems';
import useOffers from '~Hooks/useOffers';
import { useApolloClient } from '@apollo/client';
import { CourseType } from '~reducers';

interface Cart {
  items: LocalItem[];

  ticketMessages: TicketMessage[];
  setTicketMessages: React.Dispatch<React.SetStateAction<TicketMessage[]>>;

  // This typing allows both regular setItems([...]) and
  // the function variant setItems(state => [...state, ...])
  setItems: React.Dispatch<React.SetStateAction<LocalItem[]>>;

  selectedItems: string[];
  setSelectedItems: React.Dispatch<React.SetStateAction<string[]>>;

  /**
   * The packaged items are the items that are ready to be sent to the backend.
   * They are the result of items that are in cart, and then have had
   * prices calculated, discounts applied and menu entries connected.
   *
   * For example, having a "two for one" offer active will result in one
   * of the items in the "items" array having its price be zero in the
   * packagedItems array.
   */
  packagedItems: CartItem[];

  appliedDiscount: CartDiscount | null;
  setAppliedDiscount: React.Dispatch<React.SetStateAction<CartDiscount | null>>;

  serviceType: SERVICE_TYPE;
  setServiceType: React.Dispatch<React.SetStateAction<SERVICE_TYPE>>;

  // Used to ignore code if we are in TSR
  useSeparateTakeAwayMenu: boolean;

  /** Connects users (e.g. from App) to orders */
  userId: string | null;
  setUserId: React.Dispatch<React.SetStateAction<string | null>>;

  /** Example #123 - Should initially be done from OrderIdManager */
  easyId: string | null;
  setEasyId: React.Dispatch<React.SetStateAction<string | null>>;

  /** Desired time for the order */
  desiredTime: string | null;
  setDesiredTime: (date: string | null) => void;
}

export interface CartDiscount {
  /** The ID of the offer that is used */
  id?: string;

  /** The type of discount (percentage or relative) */
  type: DISCOUNT_TYPE;

  /** The amount to discount */
  value: number;

  /** Which items to apply the discount to */
  itemIds?: string[];

  /** title */
  title?: string;
}

const Context = createContext<Cart>({
  items: [],

  ticketMessages: [],
  setTicketMessages: () => {},

  setItems: () => {},

  selectedItems: [],
  setSelectedItems: () => {},

  appliedDiscount: null,
  setAppliedDiscount: () => {},

  packagedItems: [],
  serviceType: '' as SERVICE_TYPE,
  setServiceType: () => {},
  useSeparateTakeAwayMenu: false,

  userId: null,
  setUserId: () => {},

  easyId: null,
  setEasyId: () => {},

  desiredTime: null,
  setDesiredTime: () => {},
});

/**
 * The CartProvider component represents one cart.
 * The regular QSR view should use one instance of the provider,
 * while the multi-QSR view should use one instance per tab.
 */

interface Props {
  children: React.ReactNode;
  /** If TSR, always default to vatRate since the SERVICE_TYPE for TSR orders is always EAT_HERE */
  isTSR?: boolean;
  useSeparateTakeAwayMenu?: boolean;
}

const CartProvider: React.FC<Props> = ({ children, isTSR, useSeparateTakeAwayMenu }) => {
  const demoMode = useSelector((state) => state.demoMode);
  let venueId = useSelector((state) => state.venueId);

  const [storageData, storageUpdate, storageLoading] = useStorage();
  const [serviceType, setServiceType] = useState<SERVICE_TYPE>(storageData.serviceType);
  const [easyId, setEasyId] = useState<string | null>(null);
  const [desiredTime, setDesiredTime] = useState<string | null>(null);

  useEffect(() => {
    if (!storageLoading) {
      setServiceType(storageData.serviceType);
    }
  }, [storageLoading]);

  if (demoMode) {
    venueId = window.DEMO_MODE_VENUE_ID;
  }

  const [discount, setDiscount] = useState<CartDiscount | null>(null);
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [items, setItems] = useState<LocalItem[]>([]);
  const [ticketMessages, setTicketMessages] = useState<TicketMessage[]>([]);
  const [userId, setUserId] = useState<string | null>(null);

  const itemsWithVatRate = useMemo(() => {
    return items.map((item) => {
      const relevantVatRate =
        serviceType === SERVICE_TYPE.TAKE_AWAY &&
        item.takeAwayVatRate !== null &&
        item.takeAwayVatRate !== undefined &&
        !isTSR
          ? item.takeAwayVatRate
          : item.vatRate;
      return { ...item, vatRate: relevantVatRate };
    });
  }, [items, serviceType, isTSR]);

  const packagedItems = useItemsPackager(
    itemsWithVatRate,
    venueId || '',
    discount?.value || null,
    discount?.type || null,
    false,
    discount?.itemIds,
    discount?.id === MEMBERSHIP_DISCOUNT_ID,
    discount?.id,
  );

  return (
    <Context.Provider
      value={{
        useSeparateTakeAwayMenu: useSeparateTakeAwayMenu || false,
        appliedDiscount: discount,
        setAppliedDiscount: setDiscount,
        items,
        setItems,
        selectedItems,
        setSelectedItems,
        setServiceType,
        packagedItems,
        serviceType,
        ticketMessages,
        setTicketMessages,
        userId,
        setUserId,
        easyId,
        setEasyId,
        desiredTime,
        setDesiredTime,
      }}>
      {children}
    </Context.Provider>
  );
};

/**
 * useCart hook - for interacting with the cart
 * @returns The raw items data as well as an API for interacting with the cart
 */
export const useCart = () => {
  // The crazy naming here is to discourage usage of the items fetched
  // at this level inside of "setItems" calls.
  // If you'd use the items variable below you can run
  // into scenarios where the items are stale. Instead, use the
  // function form setItems(items => ...);
  const {
    items: _dangerousItemsPreferNotuse,
    setItems,
    selectedItems,
    setSelectedItems,
    packagedItems,
    appliedDiscount,
    setAppliedDiscount,
    serviceType,
    setServiceType,
    useSeparateTakeAwayMenu,
    ticketMessages,
    setTicketMessages,
    userId,
    setUserId,
    easyId,
    setEasyId,
    desiredTime,
    setDesiredTime,
  } = useContext(Context);

  const posMenu = useSelector((state) => state.POSMenu);
  const takeAwayMenu = useSelector((state) => state.POSTakeAwayMenu);

  const { offers } = useOffers();
  const client = useApolloClient();
  const venueId = useSelector((state) => state.venueId);

  const { i18n } = useI18n();

  /** Calculate the total of the cart */
  const totalBeforeDiscounts = packagedItems.reduce((acc, item) => acc + item.amount, 0);

  /** Calculate the discount of the cart */
  const discount = packagedItems.reduce((acc, item) => acc + item.discount, 0);

  /** Calculate the subtotal of the cart */
  const subtotal = packagedItems.reduce((sum, item) => {
    const price = item.amount - item.discount;
    return sum + price / (1 + item.vatRate / 100);
  }, 0);

  function updateItem(items: LocalItem[], id: string, data: Partial<LocalItem>) {
    return items.map((item) => {
      if (item.id === id) {
        return {
          ...item,
          ...data,
        };
      }

      return item;
    });
  }

  const API = {
    /**
     * Adds an item to the cart
     * @param item The item to add
     */
    addItem: (item: LocalItem) => {
      if (!useSeparateTakeAwayMenu) {
        setItems((items) => [...items, item]);
        return;
      }

      const isServiceCharge = isServiceChargeItem(item.title);

      // manully priced items should just be added, no need to check for take away menu
      if (isServiceCharge || item.manualPrice) {
        setItems((items) => [...items, item]);
        return;
      }

      if (!takeAwayMenu || serviceType !== SERVICE_TYPE.TAKE_AWAY) {
        // If the company does not use take away menus, we can just add the item
        setItems((items) => [...items, item]);
        return;
      }

      if (serviceType === SERVICE_TYPE.TAKE_AWAY) {
        const takeAwayItem = takeAwayMenu.menuEntries.find(
          (entry) => entry.item.id === item.itemId,
        );

        // if the item is a volume item, we need to calculate the price based on the volume
        if (takeAwayItem && item.volumeSuffix) {
          const number = item.title.match(/^\d+(\.\d+)?/);
          const count = number ? parseFloat(number[0]) : 1;
          const price = takeAwayItem ? takeAwayItem.price : item.price;
          const newPrice = price ? price * count : item.price;

          setItems((items) => [...items, { ...item, price: newPrice }]);
          return;
        }

        if (takeAwayItem) {
          setItems((items) => [...items, { ...item, price: takeAwayItem.price }]);
        } else {
          setItems((items) => [...items, item]);
        }
      }
    },

    /**
     * Removes an item from the cart
     * @param id The Id of the item to remove
     */
    removeItem: (id: string) => {
      setItems((items) => items.filter((item) => item.id !== id));

      // Remove the item from the selected items
      setSelectedItems((selected) => selected.filter((i) => i !== id));
    },

    /**
     * Removes multiple items from cart
     * @param ids The IDs of the items to remove
     */
    removeItems: (ids: string[]) => {
      setItems((items) => items.filter((item) => !ids.includes(item.id)));

      // Remove the items from the selected items
      setSelectedItems((selected) => selected.filter((id) => !ids.includes(id)));
    },

    /**
     * Clears the cart
     */
    clear: () => {
      setItems([]);
      setSelectedItems([]);
      setAppliedDiscount(null);
      setUserId(null);
    },

    /**
     * Sets a message on an item
     * @param id The ID of the item to update
     * @param message The message to set
     */
    setItemMessage: (id: string, message: string) => {
      setItems((items) => updateItem(items, id, { message }));
    },

    /**
     * Duplicate an item in the cart
     * @param id The ID of the item to duplicate
     * @param newId The ID of the new item
     */
    duplicateItem: (id: string, newId: string) => {
      setItems((items) => {
        const item = items.find((i) => i.id === id);

        if (item) {
          return [...items, { ...item, id: newId }];
        }

        return items;
      });
    },

    /**
     * Sets the desired discount on an item
     * @param id The ID of the item to update
     * @param discount The discount to set
     * @param discountTitle The title of the discount
     */
    setItemDiscount: (id: string, discount: number, discountTitle?: string) => {
      setItems((items) => updateItem(items, id, { discount, discountTitle }));
    },

    /**
     * Sets the item's course type
     * @param id The ID of the item to update
     * @param courseType The course type to set
     */
    setItemCourseType: (id: string, courseType: CourseType) => {
      setItems((items) => updateItem(items, id, { courseType }));
    },

    /**
     * If the item is not selected, it will become selected, and vice versa.
     * @param id The ID of the item to update
     */
    toggleSelectedItem: (
      item: CartItem | CombinedItem<CartItem> | LocalItem,
      compressedCart: boolean,
    ) => {
      const itemsToSelect = selectItem({
        selectedItem: item,
        items: packagedItems,
        selectedItems,
        compressedCart,
      });

      setSelectedItems(itemsToSelect);
    },

    /**
     * Sets the service type
     * @param type The service type to set
     */
    updateServiceType: (type: SERVICE_TYPE) => {
      // This is to handle if prices differ between take away and eat here
      if (type === SERVICE_TYPE.TAKE_AWAY && takeAwayMenu && useSeparateTakeAwayMenu) {
        setItems((items) =>
          items.reduce((acc: LocalItem[], item) => {
            // Manually priced items should be returned as is
            if (item.manualPrice) {
              return [...acc, item];
            }

            const takeAwayItem = takeAwayMenu.menuEntries.find(
              (entry) => entry.item.id === item.itemId,
            );

            // if the item is a volume item, we need to calculate the price based on the volume
            if (takeAwayItem && item.volumeSuffix) {
              const number = item.title.match(/^\d+(\.\d+)?/);
              const count = number ? parseFloat(number[0]) : 1;
              const price = takeAwayItem ? takeAwayItem.price : item.price;
              const newPrice = price ? price * count : item.price;

              return [...acc, { ...item, price: newPrice }];
            }

            if (takeAwayItem) {
              return [...acc, { ...item, price: takeAwayItem.price }];
            } else {
              return [...acc, item];
            }
          }, []),
        );
      } else if (
        type === SERVICE_TYPE.EAT_HERE &&
        takeAwayMenu &&
        posMenu &&
        useSeparateTakeAwayMenu
      ) {
        //If we have take away menus but service type is eat here, we need to update the prices to the POS menu
        setItems((items) =>
          items.reduce((acc: LocalItem[], item) => {
            // Manually priced items should be returned as is
            if (item.manualPrice) {
              return [...acc, item];
            }

            const posItem = posMenu.menuEntries.find((entry) => entry.item.id === item.itemId);

            // if the item is a volume item, we need to calculate the price based on the volume
            if (posItem && item.volumeSuffix) {
              const number = item.title.match(/^\d+(\.\d+)?/);
              const count = number ? parseFloat(number[0]) : 1;
              const price = posItem ? posItem.price : item.price;
              const newPrice = price ? price * count : item.price;

              return [...acc, { ...item, price: newPrice }];
            }

            // We clear discount to make sure the discount will not be incorrect, otherwise we will have to recalculate the discount
            return posItem ? [...acc, { ...item, price: posItem.price, discount: 0 }] : acc;
          }, []),
        );
      }

      // If we don't have a take away menu at all we do not need to do anything
      setServiceType(type);
    },

    /**
     * Adds a message to a ticket
     * @param id The ID of the ticket
     * @param message The message to add
     */
    addMessageToTicket: (id: string, message: string, courseId?: string) => {
      setTicketMessages((messages) => [...messages, { id, message, courseId }]);
    },

    removeMessagesFromTicket: (ids?: string[]) => {
      if (!ids) {
        setTicketMessages([]);
        return;
      }

      setTicketMessages((messages) => messages.filter((message) => !ids.includes(message.id)));
    },

    /**
     * Sets the user ID
     * @param id The user ID to set
     */
    setUserId: (id: string | null) => {
      setUserId(id);
    },

    /**
     * Clears all user data (userId and discounts)
     */
    clearUserData: () => {
      setUserId(null);
      setAppliedDiscount(null);
    },

    /**
     * Sets the easy ID
     * Should initially be done from OrderIdManager
     * @param id The easy ID to set
     */
    setEasyId: (id: string | null) => {
      setEasyId(id);
    },

    /**
     * Sets the desired time for the order
     * @param date The desired time to set
     */
    setDesiredTime: (date: string | null) => {
      setDesiredTime(date);
    },
  };

  const selectedItemsApi = {
    /**
     * Clears all selected items
     */
    clear: () => {
      setSelectedItems([]);
    },

    /**
     * Checks if an item is selected
     */
    has: (id: string) => {
      return selectedItems.includes(id);
    },

    /**
     * Removes all selected items from the cart
     */
    remove: () => {
      API.removeItems(selectedItems);
    },

    /**
     * Sets a message on all selected items
     * @param message The message to set
     */
    setMessage: (message: string) => {
      selectedItems.forEach((id) => API.setItemMessage(id, message));
    },

    /**
     * Duplicate all selected items in the cart
     */
    duplicate: () => {
      // Add hash for unique ID
      const hash = uuid().substring(0, 6);
      selectedItems.forEach((id) => API.duplicateItem(id, `${id}-duplicate-${hash}`));
    },

    /**
     * Sets the desired discount on all selected items
     * @param discount The discount to set
     */
    setDiscount: (discount: number) => {
      selectedItems.forEach((id) => API.setItemDiscount(id, discount));
    },

    /**
     * Applies a discount to all selected items. The difference
     * between set and apply is that apply takes a CartDiscount
     * while set takes a number.
     */
    applyDiscount: (discount: CartDiscount) => {
      // Map old DISCOUNT_TYPE to new NewDiscountType
      const discountTypeMap: Record<DISCOUNT_TYPE, NewDiscountType> = {
        [DISCOUNT_TYPE.FIXED]: NewDiscountType.FIXED,
        [DISCOUNT_TYPE.PERCENTAGE]: NewDiscountType.PERCENTAGE,
        [DISCOUNT_TYPE.PRICE]: NewDiscountType.RELATIVE,
      };

      const rawOffer = offers.find((o) => o.id === discount.id);

      // Create OrderDiscountOffer with constructor parameters
      const offer = new OrderDiscountOffer({
        discountType: discountTypeMap[discount.type],
        discountRate: discount.value,
        allowedCategoryIds: rawOffer ? rawOffer.categories.map((c) => c.id) : [],
        allowedItemIds: rawOffer ? rawOffer.items.map((i) => i.id) : [],
      });

      // Add items to the offer, skipping items in venue-level discount excluded categories
      const excludedCategoryIds = getDiscountExcludedCategoryIds(venueId || '', client);

      selectedItems.forEach((id) => {
        const item = packagedItems.find((i) => i.id === id);

        if (item) {
          // Skip items in discount excluded categories
          if (excludedCategoryIds.includes(item.categoryId || '')) {
            return;
          }

          offer.addItem({
            id,
            itemId: item.itemId || id,
            categoryId: item.categoryId || '',
            amount: item.amount,
          });
        }
      });

      // Use the new calculate() API
      const { changedItems, unchangedItems } = offer.calculate();

      // Apply discounts to changed items
      changedItems.forEach((item) => {
        API.setItemDiscount(
          item.id,
          item.discount,
          i18n(rawOffer?.nameLang) || i18n('Discount.Manual'),
        );
      });

      // Reset discount for unchanged items (if any were previously discounted)
      unchangedItems.forEach((item) => {
        API.setItemDiscount(item.id, 0, undefined);
      });
    },

    /**
     * Change course type for all the selected items
     */
    changeCourseType: (courseType: CourseType) => {
      selectedItems.forEach((id) => API.setItemCourseType(id, courseType));
    },
  };

  return {
    /**
     * The items in the cart.
     */
    items: _dangerousItemsPreferNotuse,

    /**
     * Update the items in the cart. Prefer using the API methods.
     */
    setItems,

    /**
     * The selected items in the cart.
     */
    selectedItems: {
      ids: selectedItems,
      items: packagedItems.filter((item) => selectedItems.includes(item.id)),
      ...selectedItemsApi,
    },

    /**
     * The items in the cart, with prices and discounts applied.
     */
    packagedItems,
    setSelectedItems,
    appliedDiscount,
    setAppliedDiscount,
    total: totalBeforeDiscounts,
    subtotal,
    discount,
    serviceType,
    ticketMessages,
    userId,
    easyId,
    desiredTime,
    ...API,
  };
};

export default CartProvider;
